import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { TimeOfDaySchema, WeekdaySchema } from './sync-schedule.js';

// ============================================================================
// Enum-Schemas (Spiegel der pgEnums in apps/api/src/db/schema.ts)
// ============================================================================

export const NotificationEventTypeSchema = z.enum([
  'reminder_due_soon',
  'reminder_overdue',
  'mention',
  'staffing_reopen',
  'handover_incomplete',
]);
export type NotificationEventType = z.infer<typeof NotificationEventTypeSchema>;

export const NotificationChannelSchema = z.enum(['immediate', 'digest', 'off']);
export type NotificationChannel = z.infer<typeof NotificationChannelSchema>;

export const NotificationCadenceSchema = z.enum(['once', 'weekly', 'daily']);
export type NotificationCadence = z.infer<typeof NotificationCadenceSchema>;

export const NotificationDeliveryStatusSchema = z.enum(['pending', 'sent', 'skipped', 'failed']);
export type NotificationDeliveryStatus = z.infer<typeof NotificationDeliveryStatusSchema>;

/** Empfänger-Rollen, die eine Regel auflösen kann. */
export const NotificationRecipientRoleSchema = z.enum([
  'project_lead',
  'manager',
  'mtb',
  'responsible',
  'mentioned',
]);
export type NotificationRecipientRole = z.infer<typeof NotificationRecipientRoleSchema>;

export const NotificationLocaleSchema = z.enum(['de', 'en']);
export type NotificationLocale = z.infer<typeof NotificationLocaleSchema>;

// ============================================================================
// Row-Schemas
// ============================================================================

export const NotificationTemplateSchema = z.object({
  template_id: UuidSchema,
  key: z.string().min(1),
  locale: NotificationLocaleSchema,
  subject: z.string().nullable(),
  intro_body: z.string().nullable(),
  cta_label: z.string().nullable(),
  footer: z.string().nullable(),
  digest_summary: z.string().nullable(),
  is_active: z.boolean(),
  updated_at: IsoDateTimeSchema,
  updated_by: UuidSchema.nullable(),
});
export type NotificationTemplate = z.infer<typeof NotificationTemplateSchema>;

export const NotificationRuleSchema = z.object({
  rule_id: UuidSchema,
  event_type: NotificationEventTypeSchema,
  channel: NotificationChannelSchema,
  cadence: NotificationCadenceSchema,
  first_immediate: z.boolean(),
  recipients: z.array(NotificationRecipientRoleSchema),
  recipient_strategy: z.string().nullable(),
  template_key: z.string().min(1),
  is_active: z.boolean(),
  updated_at: IsoDateTimeSchema,
  updated_by: UuidSchema.nullable(),
});
export type NotificationRule = z.infer<typeof NotificationRuleSchema>;

export const NotificationEventSchema = z.object({
  event_id: UuidSchema,
  event_type: NotificationEventTypeSchema,
  project_id: UuidSchema,
  payload: z.unknown().nullable(),
  triggered_by: UuidSchema.nullable(),
  created_at: IsoDateTimeSchema,
  processed_at: IsoDateTimeSchema.nullable(),
});
export type NotificationEvent = z.infer<typeof NotificationEventSchema>;

export const NotificationDeliverySchema = z.object({
  delivery_id: UuidSchema,
  event_id: UuidSchema,
  recipient_user_id: UuidSchema,
  channel: NotificationChannelSchema,
  status: NotificationDeliveryStatusSchema,
  digest_window: z.string().nullable(),
  dedup_key: z.string().min(1),
  error: z.string().nullable(),
  attempts: z.number().int().nonnegative(),
  sent_at: IsoDateTimeSchema.nullable(),
  created_at: IsoDateTimeSchema,
});
export type NotificationDelivery = z.infer<typeof NotificationDeliverySchema>;

// ============================================================================
// Input-Schemas (Admin-API)
// ============================================================================

/** Vorlagen-Update: alle Textfelder editierbar. `subject` ist Pflicht (nicht leer). */
export const UpdateTemplateInputSchema = z.object({
  subject: z.string().min(1),
  intro_body: z.string().min(1),
  cta_label: z.string().min(1),
  footer: z.string().min(1),
  digest_summary: z.string().nullable().optional(),
});
export type UpdateTemplateInput = z.infer<typeof UpdateTemplateInputSchema>;

/** Regel-Update: Kanal/Takt/Empfänger pflegbar. */
export const UpdateRuleInputSchema = z.object({
  channel: NotificationChannelSchema,
  cadence: NotificationCadenceSchema,
  first_immediate: z.boolean(),
  recipients: z.array(NotificationRecipientRoleSchema),
  template_key: z.string().min(1),
  is_active: z.boolean(),
});
export type UpdateRuleInput = z.infer<typeof UpdateRuleInputSchema>;

// ============================================================================
// Digest-Versand-Einstellungen (#20) — Single-Row `notification_digest_settings`
// ============================================================================

/**
 * Update-Body PUT /v1/admin/notifications/digest-settings. `weekdays` nach
 * JS Date.getDay() (0=So … 6=Sa), min. 1 Tag — Abschalten geht über `enabled`.
 */
export const UpdateDigestSettingsInputSchema = z.object({
  enabled: z.boolean(),
  time: TimeOfDaySchema,
  weekdays: z.array(WeekdaySchema).min(1).max(7),
});
export type UpdateDigestSettingsInput = z.infer<typeof UpdateDigestSettingsInputSchema>;

/** Antwort GET /v1/admin/notifications/digest-settings (effektive Einstellungen). */
export const DigestSettingsSchema = UpdateDigestSettingsInputSchema.extend({
  // Zeitzone der Versand-Uhrzeit (aus der Server-Config, read-only).
  timezone: z.string(),
  updated_at: IsoDateTimeSchema.nullable(),
  updated_by: UuidSchema.nullable(),
});
export type DigestSettings = z.infer<typeof DigestSettingsSchema>;

// ============================================================================
// Handlungshinweise (Spec 2026-09-16, §3.3/§3.6)
// ============================================================================

/** Art einer Faelligkeits-Erinnerung — was das Datum bedeutet. */
export const ReminderKindSchema = z.enum([
  'erstgespraech',
  'kickoff',
  'fertigstellung',
  'wiedervorlage',
]);
export type ReminderKind = z.infer<typeof ReminderKindSchema>;

/** Zeile der Handlungstext-Matrix: die vier Arten plus Handover (Uebergabefelder leer). */
export const ActionKindSchema = z.enum([
  'erstgespraech',
  'kickoff',
  'fertigstellung',
  'wiedervorlage',
  'handover',
]);
export type ActionKind = z.infer<typeof ActionKindSchema>;

/** Abgeleitete Erinnerung eines Projekts (`GET /v1/projects/:id` → `reminder`). */
export const ProjectReminderSchema = z.discriminatedUnion('event', [
  z.object({
    event: z.enum(['reminder_overdue', 'reminder_due_soon']),
    kind: ReminderKindSchema,
    due_date: z.string().date(),
  }),
  z.object({
    event: z.literal('handover_incomplete'),
    kind: z.literal('handover'),
    due_date: z.null(),
  }),
]);
export type ProjectReminder = z.infer<typeof ProjectReminderSchema>;

/** Update-Body PUT /v1/admin/notifications/actions/:kind/:locale. */
export const UpdateActionTextInputSchema = z.object({
  label: z.string().nullable(),
  action_overdue: z.string().nullable(),
  action_due_soon: z.string().nullable(),
});
export type UpdateActionTextInput = z.infer<typeof UpdateActionTextInputSchema>;
